import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  MousePointerClick, Users, Target, FileCheck, CheckCircle2,
  DollarSign, TrendingUp, ArrowRight, ChevronDown
} from 'lucide-react';
import api from '../lib/api';

export default function ConversionFunnelPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [affiliateFilter, setAffiliateFilter] = useState('');
  const [affiliates, setAffiliates] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [funnelData, affData] = await Promise.all([
        api.getFunnelStats(affiliateFilter || undefined, parseInt(days)),
        api.getAffiliates().catch(() => []),
      ]);
      setStats(funnelData);
      setAffiliates(affData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [days, affiliateFilter]);

  useEffect(() => { load(); }, [load]);

  if (loading && !stats) {
    return <div className="text-center py-20 text-muted-foreground">Loading funnel data...</div>;
  }

  const funnel = stats?.funnel || [];
  const conv = stats?.conversions || {};
  const comm = stats?.commissions || {};
  const breakdown = stats?.affiliate_breakdown || [];

  return (
    <div className="space-y-6" data-testid="conversion-funnel-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Conversion Funnel</h1>
          <p className="text-sm text-muted-foreground mt-1">Click &rarr; Lead &rarr; Qualified &rarr; Application &rarr; Boarded</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36" data-testid="funnel-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Select value={affiliateFilter} onValueChange={setAffiliateFilter}>
            <SelectTrigger className="w-48" data-testid="funnel-affiliate-select">
              <SelectValue placeholder="All Affiliates" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Affiliates</SelectItem>
              {affiliates.map(a => (
                <SelectItem key={a.affiliate_code} value={a.affiliate_code}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Visual Funnel */}
      <Card className="p-6" data-testid="funnel-visual">
        <div className="flex items-stretch justify-between gap-2">
          {funnel.map((stage, idx) => {
            const maxCount = Math.max(...funnel.map(s => s.count), 1);
            const heightPct = Math.max(20, (stage.count / maxCount) * 100);
            const nextStage = funnel[idx + 1];
            const convRate = nextStage && stage.count > 0
              ? ((nextStage.count / stage.count) * 100).toFixed(1)
              : null;

            return (
              <React.Fragment key={stage.name}>
                <div className="flex-1 flex flex-col items-center" data-testid={`funnel-stage-${stage.name.toLowerCase()}`}>
                  <div
                    className="w-full rounded-t-lg transition-all duration-500 flex items-end justify-center relative"
                    style={{
                      backgroundColor: stage.color + '18',
                      borderBottom: `4px solid ${stage.color}`,
                      height: `${heightPct * 1.8}px`,
                      minHeight: '50px',
                    }}
                  >
                    <span
                      className="text-2xl sm:text-3xl font-bold pb-2"
                      style={{ color: stage.color }}
                    >
                      {stage.count.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs font-semibold mt-2 text-center">{stage.name}</p>
                </div>
                {idx < funnel.length - 1 && (
                  <div className="flex flex-col items-center justify-center px-1 min-w-[50px]">
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    {convRate !== null && (
                      <span className="text-[10px] font-bold text-muted-foreground mt-0.5">{convRate}%</span>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </Card>

      {/* Conversion Rates + Commission Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Conversion Rates */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" /> Conversion Rates
          </h3>
          <div className="space-y-3">
            <ConversionRow label="Click &rarr; Lead" value={conv.click_to_lead} />
            <ConversionRow label="Lead &rarr; Qualified" value={conv.lead_to_qualified} />
            <ConversionRow label="Qualified &rarr; App" value={conv.qualified_to_application} />
            <ConversionRow label="App &rarr; Boarded" value={conv.application_to_boarded} />
            <div className="border-t pt-2 mt-2">
              <ConversionRow label="Overall (Click &rarr; Board)" value={conv.overall_click_to_board} highlight />
            </div>
          </div>
        </Card>

        {/* Commission Summary */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-green-600" /> Commission Summary
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-xl font-bold text-green-700">${comm.total?.toLocaleString() || '0'}</p>
              <p className="text-[10px] text-green-600 uppercase font-medium">Total</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xl font-bold text-blue-700">${comm.paid?.toLocaleString() || '0'}</p>
              <p className="text-[10px] text-blue-600 uppercase font-medium">Paid</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <p className="text-xl font-bold text-amber-700">${comm.pending?.toLocaleString() || '0'}</p>
              <p className="text-[10px] text-amber-600 uppercase font-medium">Pending</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Affiliate Leaderboard */}
      {breakdown.length > 0 && (
        <Card className="p-5" data-testid="affiliate-leaderboard">
          <h3 className="text-sm font-semibold mb-4">Affiliate Leaderboard</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 font-medium">Affiliate</th>
                  <th className="pb-2 font-medium text-center">Code</th>
                  <th className="pb-2 font-medium text-center">Clicks</th>
                  <th className="pb-2 font-medium text-center">Leads</th>
                  <th className="pb-2 font-medium text-center">Boarded</th>
                  <th className="pb-2 font-medium text-right">Commissions</th>
                </tr>
              </thead>
              <tbody>
                {breakdown
                  .sort((a, b) => (b.total_leads || 0) - (a.total_leads || 0))
                  .map((aff, idx) => (
                    <tr key={aff.affiliate_id || idx} className="border-b last:border-0 hover:bg-muted/30" data-testid={`leaderboard-row-${idx}`}>
                      <td className="py-2.5 font-medium">{aff.name}</td>
                      <td className="py-2.5 text-center"><Badge variant="outline" className="font-mono text-xs">{aff.affiliate_code}</Badge></td>
                      <td className="py-2.5 text-center">{aff.total_clicks || 0}</td>
                      <td className="py-2.5 text-center">{aff.total_leads || 0}</td>
                      <td className="py-2.5 text-center">{aff.total_boarded || 0}</td>
                      <td className="py-2.5 text-right font-medium">${(aff.total_commissions || 0).toLocaleString()}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function ConversionRow({ label, value, highlight }) {
  const pct = value || 0;
  const color = pct >= 50 ? 'text-green-600' : pct >= 20 ? 'text-amber-600' : pct >= 1 ? 'text-orange-600' : 'text-muted-foreground';

  return (
    <div className={`flex items-center justify-between ${highlight ? 'font-semibold' : ''}`}>
      <span className="text-sm" dangerouslySetInnerHTML={{ __html: label }} />
      <div className="flex items-center gap-2">
        <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: pct >= 50 ? '#10b981' : pct >= 20 ? '#f59e0b' : '#ef4444' }}
          />
        </div>
        <span className={`text-sm font-mono ${color}`}>{pct}%</span>
      </div>
    </div>
  );
}
